import { MakeupStatus, type GetMakeupResponse } from "@/openapi/generated";
import {
  DEFAULT_MAKEUPS_ANNUAL_PERIOD,
  getSeasonStartYear,
  type MakeupsAnnualPeriod,
  MAKEUPS_ANNUAL_PERIOD_CALENDAR,
  MAKEUPS_ANNUAL_PERIOD_SEASONAL,
} from "./makeupAnnualPeriod";
import {
  isMakeupsNoAnnualLimit,
  MAKEUPS_NO_ANNUAL_LIMIT,
} from "./makeupAnnualLimitSettings";

/** Statuses that consume a student's annual makeup request quota. */
export const MAKEUP_ANNUAL_LIMIT_COUNTABLE_STATUSES: ReadonlySet<MakeupStatus> =
  new Set([
    MakeupStatus.Pending,
    MakeupStatus.Approved,
    MakeupStatus.ChangeRequested,
  ]);

export function calendarYearDateRange(year: number = new Date().getFullYear()): {
  fromDate: Date;
  toDate: Date;
} {
  return {
    fromDate: new Date(year, 0, 1, 0, 0, 0, 0),
    toDate: new Date(year, 11, 31, 23, 59, 59, 999),
  };
}

export function seasonalYearDateRange(
  seasonStartYear: number = getSeasonStartYear(),
): {
  fromDate: Date;
  toDate: Date;
} {
  return {
    fromDate: new Date(seasonStartYear, 8, 1, 0, 0, 0, 0),
    toDate: new Date(seasonStartYear + 1, 7, 31, 23, 59, 59, 999),
  };
}

export function makeupAnnualPeriodDateRange(
  period: MakeupsAnnualPeriod = DEFAULT_MAKEUPS_ANNUAL_PERIOD,
  referenceDate: Date = new Date(),
): {
  fromDate: Date;
  toDate: Date;
} {
  if (period === MAKEUPS_ANNUAL_PERIOD_SEASONAL) {
    return seasonalYearDateRange(getSeasonStartYear(referenceDate));
  }
  return calendarYearDateRange(referenceDate.getFullYear());
}

function makeupSessionStudentId(session: GetMakeupResponse): number | null {
  const id = Number(session.student?.id as unknown);
  return Number.isFinite(id) && id > 0 ? id : null;
}

function isMakeupSessionInAnnualPeriod(
  session: GetMakeupResponse,
  period: MakeupsAnnualPeriod,
  referenceDate: Date,
): boolean {
  const requestedAt =
    session.requestedAt instanceof Date
      ? session.requestedAt
      : new Date(session.requestedAt);

  if (period === MAKEUPS_ANNUAL_PERIOD_SEASONAL) {
    return (
      getSeasonStartYear(requestedAt) === getSeasonStartYear(referenceDate)
    );
  }

  return requestedAt.getFullYear() === referenceDate.getFullYear();
}

export function isMakeupSessionCountableForAnnualLimit(
  session: GetMakeupResponse,
): boolean {
  return MAKEUP_ANNUAL_LIMIT_COUNTABLE_STATUSES.has(session.status);
}

export function countAnnualMakeupRequestsForStudent(
  sessions: GetMakeupResponse[],
  studentId: number,
  period: MakeupsAnnualPeriod = DEFAULT_MAKEUPS_ANNUAL_PERIOD,
  referenceDate: Date = new Date(),
): number {
  return sessions.filter((session) => {
    if (makeupSessionStudentId(session) !== studentId) return false;
    if (!isMakeupSessionCountableForAnnualLimit(session)) return false;
    return isMakeupSessionInAnnualPeriod(session, period, referenceDate);
  }).length;
}

export function buildAnnualMakeupUsageByStudent(
  sessions: GetMakeupResponse[],
  studentIds: number[],
  period: MakeupsAnnualPeriod = DEFAULT_MAKEUPS_ANNUAL_PERIOD,
  referenceDate: Date = new Date(),
): Map<number, number> {
  const usage = new Map<number, number>();
  for (const studentId of studentIds) {
    usage.set(
      studentId,
      countAnnualMakeupRequestsForStudent(
        sessions,
        studentId,
        period,
        referenceDate,
      ),
    );
  }
  return usage;
}

export function isStudentAtAnnualMakeupLimit(
  used: number,
  limit: number = MAKEUPS_NO_ANNUAL_LIMIT,
): boolean {
  if (isMakeupsNoAnnualLimit(limit)) return false;
  return used >= limit;
}

export function remainingAnnualMakeupRequests(
  used: number,
  limit: number = MAKEUPS_NO_ANNUAL_LIMIT,
): number | null {
  if (isMakeupsNoAnnualLimit(limit)) return null;
  return Math.max(0, limit - used);
}
